import React, { useState, useMemo, useRef } from 'react';
import './Devtool.scss';
import { DirectionEnum } from '../../types/enums';
import { tileWidth, tileHeight } from '../../data/tileset.config';
import { dungeonService } from '../../services/DungeonService';
import { MonsterEntity } from '../../entities/MonsterEntity';
import { PlayerEntity } from '../../entities/PlayerEntity';
import { MONSTER_CONFIGS } from '../../data/monsters.config';
import { useTick } from '@pixi/react';
import { useGameStore } from '../../store/gameSlice';
import { useDevtoolStore } from '../../store/devtoolSlice';
import { useDevtoolDraggable } from '../../hooks/useDevtoolDraggable';

export const Devtool: React.FC = () => {
  const gameplay = useGameStore((s) => s.gameplay);
  const devtoolPosition = useDevtoolStore((s) => s.devtoolPosition);
  const setDevtoolPosition = useDevtoolStore((s) => s.setDevtoolPosition);
  const panelRef = useRef<HTMLDivElement>(null);
  const { onPointerDown: onHeaderPointerDown } = useDevtoolDraggable({
    position: devtoolPosition,
    setPosition: setDevtoolPosition,
    getSize: () => ({
      width: panelRef.current?.offsetWidth ?? 300,
      height: panelRef.current?.offsetHeight ?? 200,
    }),
  });
  const disabled = gameplay !== 'dungeon';
  const entityTick = useDevtoolStore((s) => s.entityTick);
  const setEntityTick = useDevtoolStore((s) => s.setEntityTick);
  const currentDelta = useGameStore((s) => s.currentDelta);
  const cameraPosition = useGameStore((s) => s.cameraPosition);
  const player = dungeonService.getPlayer();
  const zoomFactor = useGameStore((s) => s.zoomFactor);
  const setZoomFactor = useGameStore((s) => s.setZoomFactor);
  const lockCameraPosition = useGameStore((s) => s.lockCameraPosition);
  const setLockCameraPosition = useGameStore((s) => s.setLockCameraPosition);
  const [selectedEntity, setSelectedEntity] = useState<
    PlayerEntity | MonsterEntity | null
  >(null);
  const [spawnerInterval, setSpawnerInterval] = useState(
    dungeonService.monsterSpawningService.getSpawnerInterval(),
  );
  const monsterIds = useMemo(() => Object.keys(MONSTER_CONFIGS).sort(), []);
  const [spawnId, setSpawnId] = useState<string>(monsterIds[0] ?? '');
  const currentFloor = useGameStore((s) => s.currentFloor);
  const [floorInput, setFloorInput] = useState<number>(currentFloor);
  const [xpInput, setXpInput] = useState<number>(50);
  const [avgDelta, setAvgDelta] = useState(1.0);
  const deltaHistory = useRef<number[]>([]);
  const updateCounter = useRef(0);

  useTick(() => {
    deltaHistory.current.push(currentDelta);

    if (deltaHistory.current.length > 100) {
      deltaHistory.current.shift();
    }

    updateCounter.current++;
    if (updateCounter.current >= 30) {
      updateCounter.current = 0;
      const sum = deltaHistory.current.reduce((acc, val) => acc + val, 0);
      const avg = sum / deltaHistory.current.length;
      setAvgDelta(avg);
    }
  });

  const teleportEntity = (entity: any, x: number, y: number) => {
    entity.setPosition(x, y);
    entity.absoluteX = x * tileWidth;
    entity.absoluteY = y * tileHeight;
  };

  const entityInfo = useMemo(() => {
    if (!selectedEntity) {
      return null;
    }

    return {
      type: selectedEntity.type,
      positionX: selectedEntity.positionX,
      positionY: selectedEntity.positionY,
      life: selectedEntity.life,
      action: selectedEntity.getActionState()?.name || 'idle',
      phase: selectedEntity.getActionState()?.phase || 'none',
      direction: selectedEntity.direction,
    };
  }, [selectedEntity]);

  const entities = dungeonService.getEntities();

  return (
    <div
      ref={panelRef}
      className={`devtool${disabled ? ' devtool--disabled' : ''}`}
      style={{ left: devtoolPosition.x, top: devtoolPosition.y }}
    >
      <div
        className="devtool-header"
        onPointerDown={onHeaderPointerDown}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'move',
        }}
      >
        <h3>DevTools</h3>
        <span
          style={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: avgDelta > 1.1 ? '#ff4444' : '#4CAF50',
          }}
        >
          Avg Delta: {avgDelta.toFixed(2)}
        </span>
      </div>

      {disabled && <div className="devtool-placeholder">Pas en donjon</div>}

      <div className="devtool-body">
        <div className="devtool-section">
          <h4>Caméra</h4>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            <button onClick={() => setZoomFactor(zoomFactor + 1)}>Zoom</button>
            <button onClick={() => setZoomFactor(Math.max(1, zoomFactor - 1))}>
              Dézoom
            </button>
            <button
              onClick={() => setLockCameraPosition(!lockCameraPosition)}
              style={{
                backgroundColor: lockCameraPosition ? '#f44336' : undefined,
              }}
            >
              {lockCameraPosition
                ? 'Caméra verrouillée'
                : 'Verrouiller la caméra'}
            </button>
          </div>
        </div>

        <div className="devtool-section">
          <h4>Run</h4>
          <div
            style={{
              display: 'flex',
              gap: '4px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <label>
              Étage :
              <input
                type="number"
                value={floorInput}
                onChange={(e) => setFloorInput(Number(e.target.value))}
                style={{ marginLeft: '4px', width: '60px' }}
              />
            </label>
            <button
              onClick={() => {
                dungeonService.emitGameEvent('goToFloor', {
                  floor: floorInput,
                });
              }}
            >
              TP étage
            </button>
            <span style={{ fontSize: '11px', opacity: 0.7 }}>
              (étage hors plage 1-9 → fin de run)
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              gap: '4px',
              alignItems: 'center',
              marginTop: '6px',
            }}
          >
            <label>
              XP :
              <input
                type="number"
                value={xpInput}
                onChange={(e) => setXpInput(Number(e.target.value))}
                style={{ marginLeft: '4px', width: '80px' }}
              />
            </label>
            <button
              onClick={() => {
                dungeonService.awardXp(xpInput);
              }}
            >
              +XP
            </button>
          </div>
        </div>

        <div className="devtool-section">
          <h4>Monster Spawner</h4>
          <label>
            <input
              type="checkbox"
              checked={dungeonService.monsterSpawningService.isSpawnerEnabled()}
              onChange={(e) =>
                dungeonService.monsterSpawningService.setSpawnerEnabled(
                  e.target.checked,
                )
              }
            />
            Enable Spawner
          </label>
          <div style={{ marginTop: '8px' }}>
            <label>
              Spawn Interval (ticks):
              <input
                type="number"
                value={spawnerInterval}
                onChange={(e) => {
                  const newInterval = Number(e.target.value);
                  setSpawnerInterval(newInterval);
                  dungeonService.monsterSpawningService.setSpawnerInterval(
                    newInterval,
                  );
                }}
                style={{ marginLeft: '8px', width: '80px' }}
                min="10"
              />
            </label>
          </div>
          <div style={{ marginTop: '8px' }}>
            <select
              value={spawnId}
              onChange={(e) => setSpawnId(e.target.value)}
              style={{ maxWidth: '160px' }}
            >
              {monsterIds.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
            <button
              style={{ marginLeft: '8px' }}
              onClick={() => {
                const pos =
                  dungeonService.monsterSpawningService.findValidSpawnPosition();
                if (!pos) {
                  return;
                }
                const monster =
                  dungeonService.monsterSpawningService.createMonster(
                    spawnId,
                    pos.x,
                    pos.y,
                    Math.max(1, currentFloor),
                  );
                if (monster) {
                  dungeonService.addEntity(monster);
                }
              }}
            >
              Spawn
            </button>
          </div>
        </div>

        <div className="devtool-section">
          <h4>Entities ({entities.length})</h4>
          <div className="entities-list">
            {entities.length === 0 ? (
              <p>No entities</p>
            ) : (
              entities.map((entity: any) => (
                <div
                  key={entity.id}
                  className={`entity-item-simple ${
                    selectedEntity?.id === entity.id ? 'selected' : ''
                  }`}
                  onClick={() => setSelectedEntity(entity)}
                >
                  <strong>{entity.id}</strong> ({entity.type}){' '}
                  {entity.isDirty ? '*' : ''}
                </div>
              ))
            )}
          </div>

          {selectedEntity && entityInfo && (
            <div className="entity-details-panel">
              <h5>🎯 {selectedEntity.id}</h5>
              <div className="entity-info">
                <div>Type: {entityInfo.type}</div>
                <div>
                  Position: ({entityInfo.positionX}, {entityInfo.positionY})
                </div>
                <div>Life: {entityInfo.life}</div>
                <div>Action: {entityInfo.action}</div>
                <div>Direction: {entityInfo.direction}</div>
                <div>Phase: {entityInfo.phase}</div>
              </div>

              {selectedEntity instanceof MonsterEntity && (
                <div className="entity-controls">
                  <h6>AI</h6>
                  <button
                    onClick={() => {
                      selectedEntity.toggleAI();
                      setSelectedEntity(selectedEntity);
                    }}
                    style={{
                      backgroundColor: selectedEntity.aiEnabled
                        ? '#4CAF50'
                        : '#f44336',
                    }}
                  >
                    AI: {selectedEntity.aiEnabled ? 'ON' : 'OFF'}
                  </button>
                </div>
              )}

              <div className="entity-controls">
                <h6>Available Actions</h6>
                {selectedEntity.getAvailableActions().map((action) => (
                  <div key={action.name} style={{ marginBottom: '8px' }}>
                    {action.parameter?.type === 'direction' ? (
                      <div>
                        <label>{action.name}:</label>
                        <select
                          id={`action-${action.name}-param`}
                          style={{ marginLeft: '8px', marginRight: '8px' }}
                        >
                          <option value={DirectionEnum.TOP}>↑ Top</option>
                          <option value={DirectionEnum.BOTTOM}>↓ Bottom</option>
                          <option value={DirectionEnum.LEFT}>← Left</option>
                          <option value={DirectionEnum.RIGHT}>→ Right</option>
                          <option value={DirectionEnum['TOP-LEFT']}>
                            ↖ Top-Left
                          </option>
                          <option value={DirectionEnum['TOP-RIGHT']}>
                            ↗ Top-Right
                          </option>
                          <option value={DirectionEnum['BOTTOM-LEFT']}>
                            ↙ Bottom-Left
                          </option>
                          <option value={DirectionEnum['BOTTOM-RIGHT']}>
                            ↘ Bottom-Right
                          </option>
                        </select>
                        <button
                          onClick={() => {
                            const select = document.getElementById(
                              `action-${action.name}-param`,
                            ) as HTMLSelectElement;
                            const direction = Number(select.value);
                            action.execute(direction);
                          }}
                        >
                          Execute
                        </button>
                      </div>
                    ) : action.parameter?.type === 'position' ? (
                      <div>
                        <label>{action.name}:</label>
                        <input
                          type="number"
                          id={`action-${action.name}-x`}
                          placeholder="X"
                          defaultValue={selectedEntity.positionX}
                          style={{
                            marginLeft: '8px',
                            width: '50px',
                          }}
                        />
                        <input
                          type="number"
                          id={`action-${action.name}-y`}
                          placeholder="Y"
                          defaultValue={selectedEntity.positionY}
                          style={{
                            marginLeft: '4px',
                            marginRight: '8px',
                            width: '50px',
                          }}
                        />
                        <button
                          onClick={() => {
                            const inputX = document.getElementById(
                              `action-${action.name}-x`,
                            ) as HTMLInputElement;
                            const inputY = document.getElementById(
                              `action-${action.name}-y`,
                            ) as HTMLInputElement;
                            const x = Number(inputX.value);
                            const y = Number(inputY.value);
                            action.execute(x, y);
                          }}
                        >
                          Execute
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => action.execute()}>
                        {action.name}
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="entity-controls">
                <h6>Teleport</h6>
                <div className="teleport-controls">
                  <input
                    type="number"
                    placeholder="X"
                    id={`teleport-x-${selectedEntity.id}`}
                    defaultValue={selectedEntity.positionX}
                  />
                  <input
                    type="number"
                    placeholder="Y"
                    id={`teleport-y-${selectedEntity.id}`}
                    defaultValue={selectedEntity.positionY}
                  />
                  <button
                    onClick={() => {
                      const x = Number(
                        (
                          document.getElementById(
                            `teleport-x-${selectedEntity.id}`,
                          ) as HTMLInputElement
                        )?.value,
                      );
                      const y = Number(
                        (
                          document.getElementById(
                            `teleport-y-${selectedEntity.id}`,
                          ) as HTMLInputElement
                        )?.value,
                      );
                      teleportEntity(selectedEntity, x, y);
                      setEntityTick(entityTick + 1);
                    }}
                  >
                    TP
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <>
        <div className="devtool-info">
          <span>{`camera : [${Math.floor(cameraPosition.x)}, ${Math.floor(
            cameraPosition.y,
          )}]`}</span>
          <span>{`player : [${player?.positionX}, ${player?.positionY}]`}</span>
          <span>{`zoom : ${Math.floor(zoomFactor)}x`}</span>
        </div>
      </>
    </div>
  );
};
